import { DEFAULT_CURRENCY, Money } from '@arabat/money';
import type { StoreProduct, Storefront } from '@arabat/contracts';
import { type ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useLocalName } from './shared.js';

/** One cart line. Prices are never stored: they are always read from the current catalog. */
export interface CartLine {
  variantId: string;
  productId: string;
  quantity: number;
  /** The shopper's options (ADR-0160); the same variant with other choices is another line. */
  optionIds?: string[];
  /** The shopper's note for this item (ADR-0160). */
  note?: string;
}

/** What makes two lines the same line: variant, options (any order) and note. */
export const lineKey = (l: Pick<CartLine, 'variantId' | 'optionIds' | 'note'>) =>
  [l.variantId, [...(l.optionIds ?? [])].sort().join(','), l.note?.trim() ?? ''].join('|');

interface CartState {
  lines: CartLine[];
  /** `max`: the most of this variant the cart may hold, over all its lines. */
  add: (line: CartLine, max: number) => void;
  /** By line key (`lineKey`). */
  setQuantity: (key: string, quantity: number) => void;
  remove: (key: string) => void;
  clear: () => void;
  count: number;
  /** Units of a variant in the cart, every line together. */
  quantityOf: (variantId: string) => number;
}

const CartContext = createContext<CartState | null>(null);
const storageKey = (slug: string) => `arabat.cart.${slug}`;

function load(slug: string): CartLine[] {
  try {
    const raw = globalThis.localStorage?.getItem(storageKey(slug));
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (l): l is CartLine =>
        typeof l === 'object' &&
        l !== null &&
        typeof (l as CartLine).variantId === 'string' &&
        typeof (l as CartLine).productId === 'string' &&
        Number.isInteger((l as CartLine).quantity) &&
        (l as CartLine).quantity > 0 &&
        ((l as CartLine).optionIds === undefined ||
          (Array.isArray((l as CartLine).optionIds) &&
            (l as CartLine).optionIds!.every((x) => typeof x === 'string'))) &&
        ((l as CartLine).note === undefined || typeof (l as CartLine).note === 'string'),
    );
  } catch {
    return [];
  }
}

/** Cart of one store, kept in this browser only (per store slug). */
export function CartProvider({ slug, children }: { slug: string; children: ReactNode }) {
  const [lines, setLines] = useState<CartLine[]>(() => load(slug));
  const update = useCallback(
    (next: (current: CartLine[]) => CartLine[]) =>
      setLines((current) => {
        const value = next(current).filter((l) => l.quantity > 0);
        try {
          globalThis.localStorage?.setItem(storageKey(slug), JSON.stringify(value));
        } catch {
          // Storage unavailable: the cart still works for this page view.
        }
        return value;
      }),
    [slug],
  );
  const value = useMemo<CartState>(
    () => ({
      lines,
      count: lines.reduce((n, l) => n + l.quantity, 0),
      quantityOf: (variantId) =>
        lines.filter((l) => l.variantId === variantId).reduce((n, l) => n + l.quantity, 0),
      add: (line, max) =>
        update((current) => {
          const key = lineKey(line);
          // The variant's other lines count against the same maximum.
          const others = current
            .filter((l) => l.variantId === line.variantId && lineKey(l) !== key)
            .reduce((n, l) => n + l.quantity, 0);
          const room = Math.max(0, max - others);
          const clean: CartLine = {
            variantId: line.variantId,
            productId: line.productId,
            quantity: line.quantity,
            ...(line.optionIds?.length ? { optionIds: line.optionIds } : {}),
            ...(line.note?.trim() ? { note: line.note.trim() } : {}),
          };
          const existing = current.find((l) => lineKey(l) === key);
          if (!existing)
            return [...current, { ...clean, quantity: Math.min(clean.quantity, room) }];
          return current.map((l) =>
            lineKey(l) === key
              ? { ...l, quantity: Math.min(l.quantity + clean.quantity, room) }
              : l,
          );
        }),
      setQuantity: (key, quantity) =>
        update((current) => current.map((l) => (lineKey(l) === key ? { ...l, quantity } : l))),
      remove: (key) => update((current) => current.filter((l) => lineKey(l) !== key)),
      clear: () => update(() => []),
    }),
    [lines, update],
  );
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartState {
  const cart = useContext(CartContext);
  if (!cart) throw new Error('useCart must be used inside <CartProvider>');
  return cart;
}

/** Wire money → exact Money (the API only sends KWD-style decimal strings). */
export const toMoney = (value: { amount: string; currency: string }) =>
  Money.fromJSON(value as { amount: string; currency: 'KWD' });

interface PricedLine extends CartLine {
  key: string;
  product: StoreProduct;
  variant: StoreProduct['variants'][number];
  /** The chosen options with their groups, in the product's order (ADR-0160). */
  options: {
    group: StoreProduct['optionGroups'][number];
    option: StoreProduct['optionGroups'][number]['options'][number];
  }[];
  /** The variant's price plus the options' prices. */
  unitPrice: Money;
  lineTotal: Money;
}

/** The options of a line as the product offers them now; null when one is no longer offered. */
export function lineOptions(product: StoreProduct, optionIds: string[] = []) {
  const found = product.optionGroups.flatMap((group) =>
    group.options.filter((o) => optionIds.includes(o.id)).map((option) => ({ group, option })),
  );
  return found.length === optionIds.length ? found : null;
}

/**
 * Prices the cart against the current catalog with exact decimal money (no floats). Lines
 * whose item is no longer listed or sold out are returned in `missing`. The server prices
 * the order again and refuses it if the total the shopper saw changed (TOTAL_CHANGED).
 */
export function priceLines(
  lines: CartLine[],
  products: StoreProduct[],
  store: Pick<Storefront, 'deliveryFee'>,
  fulfillment: 'pickup' | 'delivery',
  /**
   * The chosen delivery area (ADR-0123): its fee, free at or above its threshold. `undefined`
   * = the store has no areas (flat fee); `null` = none chosen yet (nothing charged yet).
   */
  area?: Storefront['deliveryAreas'][number] | null,
) {
  const priced: PricedLine[] = [];
  const missing: CartLine[] = [];
  for (const line of lines) {
    const product = products.find((p) => p.productId === line.productId);
    const variant = product?.variants.find((v) => v.variantId === line.variantId);
    const options = product ? lineOptions(product, line.optionIds) : null;
    if (!product || !variant || !variant.available || !options) {
      missing.push(line);
      continue;
    }
    const unitPrice = options.reduce(
      (sum, o) => sum.add(toMoney(o.option.price)),
      toMoney(variant.price),
    );
    priced.push({
      ...line,
      key: lineKey(line),
      options,
      quantity: Math.min(line.quantity, variant.maxQuantity),
      product,
      variant,
      unitPrice,
      lineTotal: unitPrice.multiply(String(Math.min(line.quantity, variant.maxQuantity))),
    });
  }
  const currency = priced[0]?.unitPrice.currency ?? DEFAULT_CURRENCY;
  const subtotal = priced.reduce((sum, l) => sum.add(l.lineTotal), Money.zero(currency));
  const deliveryFee =
    fulfillment !== 'delivery'
      ? Money.zero(currency)
      : area === undefined
        ? toMoney(store.deliveryFee)
        : area === null
          ? Money.zero(currency)
          : area.freeDeliveryFrom !== null && subtotal.compare(toMoney(area.freeDeliveryFrom)) >= 0
            ? Money.zero(currency)
            : toMoney(area.fee);
  return { priced, missing, subtotal, deliveryFee, total: subtotal.add(deliveryFee) };
}

/** Two products standing in for an empty cart inside the console's preview (never ordered). */
export function sampleLines(products: StoreProduct[]): CartLine[] {
  return products
    .filter((p) => p.variants.some((v) => v.available))
    .slice(0, 2)
    .map((p) => {
      const v = p.variants.find((x) => x.available)!;
      return { variantId: v.variantId, productId: p.productId, quantity: 1 };
    });
}

/** A priced line as the quote and checkout send it (ADR-0160: with its options and note). */
export const checkoutLine = (l: CartLine) => ({
  variantId: l.variantId,
  quantity: String(l.quantity),
  ...(l.optionIds?.length ? { optionIds: l.optionIds } : {}),
  ...(l.note?.trim() ? { note: l.note.trim() } : {}),
});

/** "Roast: Espresso · Grind: Ground" and the note under a cart line (ADR-0160). */
export function LineChoices({ line }: { line: Pick<PricedLine, 'options' | 'note'> }) {
  const name = useLocalName();
  if (line.options.length === 0 && !line.note) return null;
  return (
    <span className="block text-xs text-fg-muted" data-testid="line-choices">
      {line.options.map((o) => `${name(o.group)}: ${name(o.option)}`).join(' · ')}
      {line.note ? <span className="block">«{line.note}»</span> : null}
    </span>
  );
}
